import React, { useState } from "react";
import { Box, Button, InputBase } from "@mui/material";
import { MAGENTA, MAGENTA_DARK } from "./tokens";
import { TOUCH_QUERY } from "../../../utils/useViewport";

interface QuickAddBarProps {
  adding: boolean;
  // Resolves once the reminder is saved; the input clears only on success.
  onSubmit: (title: string) => Promise<boolean>;
}

// The typed text is local state here on purpose: keeping it in
// EngagementsDashboard re-rendered the whole feed (every FeedRow, the tiles,
// the Recharts card) on every keystroke, which made typing lag badly.
const QuickAddBar: React.FC<QuickAddBarProps> = ({ adding, onSubmit }) => {
  const [text, setText] = useState("");

  const submit = async () => {
    const title = text.trim();
    if (!title || adding) return;
    if (await onSubmit(title)) setText("");
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <InputBase
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") submit();
        }}
        placeholder="Quick add a reminder…"
        sx={{
          height: 34,
          width: 210,
          padding: "0 10px",
          borderRadius: "8px",
          border: "1px solid #d9d9dd",
          fontSize: "13px",
          backgroundColor: "#fff",
          "&.Mui-focused": { borderColor: MAGENTA },
          // iOS zooms into inputs under 16 px on focus.
          [`@media ${TOUCH_QUERY}`]: { height: 40, fontSize: "16px" },
        }}
      />
      <Button
        onClick={submit}
        disabled={adding || !text.trim()}
        sx={{
          height: 34,
          minWidth: 0,
          padding: "0 14px",
          borderRadius: "8px",
          backgroundColor: MAGENTA,
          color: "#fff",
          fontWeight: 600,
          textTransform: "none",
          "&:hover": { backgroundColor: MAGENTA_DARK },
          "&.Mui-disabled": { backgroundColor: "#e2e2e6", color: "#fff" },
          [`@media ${TOUCH_QUERY}`]: { height: 40 },
        }}
      >
        Add
      </Button>
    </Box>
  );
};

export default React.memo(QuickAddBar);
